import {
  CreditCardOutlined,
  EditOutlined,
  FileTextOutlined,
  SafetyOutlined
} from "@ant-design/icons"
import { Button, Card, Col, Flex, Row, Typography, theme } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { asArray } from "../../helpers/list-values.ts"
import {
  SubcontractorSectionForm,
  type SubcontractorEditSection
} from "../../components/SubcontractorSectionForm.tsx"
import { type SubcontractorFormData } from "../../components/SubcontractorForm/subcontractor-form-data.ts"
import { type SubcontractorListItem } from "../../types/types.ts"
import { DetailField } from "./DetailField.tsx"

const { Title } = Typography

/**
 * A Tax & Payment section: its own white card, and its own read/edit state so
 * one section can be edited while the others stay readable.
 */
const SectionCard = ({
  icon,
  title,
  canEdit,
  editing,
  onEdit,
  children
}: {
  icon: React.ReactNode
  title: string
  canEdit: boolean
  editing: boolean
  onEdit: () => void
  children: React.ReactNode
}) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  return (
    <Card
      // An antd Card rather than a bare element: the page's other cards are
      // antd's, and the padding the mockup wants is a `styles` override, not
      // a different component.
      styles={{
        header: {
          padding: "16px 24px",
          minHeight: "auto",
          borderBottom: "1px solid #F3F4F6"
        },
        body: { padding: "20px 24px" }
      }}
      style={{ borderRadius: 12 }}
      title={
        <Flex align={"center"} gap={12} className={"min-w-0"}>
          <Flex
            aria-hidden
            align={"center"}
            justify={"center"}
            className={"size-10 shrink-0 rounded-lg text-lg"}
            style={{
              background: token.colorPrimaryBg,
              color: token.colorPrimary
            }}
          >
            {icon}
          </Flex>
          <Title level={5} className={"!mb-0 truncate !text-lg"}>
            {title}
          </Title>
        </Flex>
      }
      extra={
        canEdit &&
        !editing && (
          <Button
            color={"primary"}
            variant={"outlined"}
            icon={<EditOutlined />}
            onClick={onEdit}
            aria-label={t("subcontractorDetail.editSection", {
              section: title
            })}
          >
            {t("subcontractorDetail.edit")}
          </Button>
        )
      }
    >
      {children}
    </Card>
  )
}

/** A row of read-only fields, separated from the next by a hairline. */
const FieldRow = ({
  children,
  last = false
}: {
  children: React.ReactNode
  last?: boolean
}) => (
  <Row
    gutter={[24, 20]}
    className={
      last
        ? undefined
        : "mb-5 border-0 border-b border-solid border-gray-100 pb-5"
    }
  >
    {children}
  </Row>
)

interface Props {
  subcontractor: SubcontractorListItem
  taxIdLabel: string
  taxIdValue?: string
  mayEditIdentity: boolean
  mayEditCslb: boolean
  mayEditPayment: boolean
  onSaveSection: (data: Partial<SubcontractorFormData>) => Promise<boolean>
}

export const TaxPaymentSections = ({
  subcontractor,
  taxIdLabel,
  taxIdValue,
  mayEditIdentity,
  mayEditCslb,
  mayEditPayment,
  onSaveSection
}: Props) => {
  const { t } = useTranslation()
  const notProvided = t("subcontractorDetail.notProvided")
  // Per section, so editing the licence leaves the other two readable.
  const [editing, setEditing] = useState<
    Record<SubcontractorEditSection, boolean>
  >({ identity: false, cslb: false, payment: false })

  const setSection = (section: SubcontractorEditSection, open: boolean) =>
    setEditing((current) => ({ ...current, [section]: open }))

  const saveSection = async (
    section: SubcontractorEditSection,
    data: Partial<SubcontractorFormData>
  ) => {
    const saved = await onSaveSection(data)
    if (saved) setSection(section, false)
    return saved
  }

  const sectionForm = (section: SubcontractorEditSection) => (
    <SubcontractorSectionForm
      section={section}
      subcontractor={subcontractor}
      onSave={(data) => saveSection(section, data)}
      onCancel={() => setSection(section, false)}
    />
  )

  return (
    <Row gutter={[24, 24]} align={"top"}>
      <Col xs={24} lg={12}>
        <Flex vertical gap={24}>
          <SectionCard
            icon={<FileTextOutlined />}
            title={t("subcontractorDetail.identityAndTax")}
            canEdit={mayEditIdentity}
            editing={editing.identity}
            onEdit={() => setSection("identity", true)}
          >
            {editing.identity ? (
              sectionForm("identity")
            ) : (
              <>
                <FieldRow>
                  <DetailField
                    label={"Legal Business Name"}
                    value={subcontractor.legal_business_name}
                    emptyText={notProvided}
                  />
                  <DetailField
                    label={"Entity Type"}
                    value={subcontractor.entity_type_display}
                    emptyText={notProvided}
                  />
                </FieldRow>
                <FieldRow>
                  <DetailField
                    label={"Business Address"}
                    value={subcontractor.business_address}
                    emptyText={notProvided}
                    md={24}
                  />
                </FieldRow>
                <FieldRow>
                  <DetailField
                    label={"City"}
                    value={subcontractor.business_city}
                    emptyText={notProvided}
                    xs={12}
                    md={8}
                  />
                  <DetailField
                    label={"State"}
                    value={subcontractor.business_state}
                    emptyText={notProvided}
                    xs={12}
                    md={8}
                  />
                  <DetailField
                    label={"ZIP Code"}
                    value={subcontractor.business_zip_code}
                    emptyText={notProvided}
                    xs={12}
                    md={8}
                  />
                </FieldRow>
                <FieldRow last>
                  <DetailField
                    label={taxIdLabel}
                    value={taxIdValue}
                    emptyText={notProvided}
                    xs={12}
                    md={8}
                  />
                  <DetailField
                    label={"W-9 Document"}
                    value={subcontractor.w9_document ? "On file" : undefined}
                    emptyText={t("subcontractorDetail.notUploaded")}
                    xs={12}
                    md={8}
                  />
                  <DetailField
                    label={"W-9 Validity Date"}
                    value={subcontractor.w9_validity_date}
                    emptyText={notProvided}
                    xs={12}
                    md={8}
                  />
                </FieldRow>
              </>
            )}
          </SectionCard>

          <SectionCard
            icon={<CreditCardOutlined />}
            title={t("subcontractorDetail.payment")}
            canEdit={mayEditPayment}
            editing={editing.payment}
            onEdit={() => setSection("payment", true)}
          >
            {editing.payment ? (
              sectionForm("payment")
            ) : (
              <FieldRow last>
                <DetailField
                  label={"Payment Method"}
                  value={subcontractor.payment_method_display}
                  emptyText={notProvided}
                />
                {subcontractor.payment_method === "ach" && (
                  <>
                    <DetailField
                      label={"ACH Routing Number"}
                      value={
                        subcontractor.ach_routing_number_last4
                          ? `On file, ending ${subcontractor.ach_routing_number_last4}`
                          : undefined
                      }
                      emptyText={notProvided}
                    />
                    <DetailField
                      label={"ACH Account Number"}
                      value={
                        subcontractor.ach_account_number_last4
                          ? `On file, ending ${subcontractor.ach_account_number_last4}`
                          : undefined
                      }
                      emptyText={notProvided}
                    />
                  </>
                )}
              </FieldRow>
            )}
          </SectionCard>
        </Flex>
      </Col>

      <Col xs={24} lg={12}>
        <SectionCard
          icon={<SafetyOutlined />}
          title={t("subcontractorDetail.cslbLicense")}
          canEdit={mayEditCslb}
          editing={editing.cslb}
          onEdit={() => setSection("cslb", true)}
        >
          {editing.cslb ? (
            sectionForm("cslb")
          ) : (
            <>
              <FieldRow>
                <DetailField
                  label={"License Number"}
                  value={subcontractor.cslb_license_number}
                  emptyText={notProvided}
                />
                <DetailField
                  label={"Classification"}
                  value={
                    asArray(subcontractor.cslb_classification).join(", ") ||
                    undefined
                  }
                  emptyText={notProvided}
                />
              </FieldRow>
              <FieldRow>
                <DetailField
                  label={"Status"}
                  value={subcontractor.cslb_status_display}
                  emptyText={notProvided}
                />
                <DetailField
                  label={"Issue Date"}
                  value={subcontractor.cslb_issue_date}
                  emptyText={notProvided}
                />
              </FieldRow>
              <FieldRow>
                <DetailField
                  label={"Bond Amount"}
                  value={subcontractor.cslb_bond_amount}
                  emptyText={notProvided}
                />
                <DetailField
                  label={"Bond Number"}
                  value={subcontractor.cslb_bond_number}
                  emptyText={notProvided}
                />
              </FieldRow>
              <FieldRow last>
                <DetailField
                  label={"Qualifying Individual Bond Amount"}
                  value={subcontractor.cslb_qualifier_bond_amount}
                  emptyText={notProvided}
                />
                <DetailField
                  label={"Qualifying Individual Bond Number"}
                  value={subcontractor.cslb_qualifier_bond_number}
                  emptyText={notProvided}
                />
              </FieldRow>
            </>
          )}
        </SectionCard>
      </Col>
    </Row>
  )
}
